import { cardText } from 'components/ExportDesign/exportDesignResultCardData';
import { reasonPhrase } from 'components/ExportDesign/exportDesignControl';

// --- The licensing answer as a card (UX S12 R-13, board 14). The backend sends it as `data.licensing_card`
// (`_licensing_card`) on the licensing answer; the markdown answer is still sent and stays the fallback. Read
// defensively: a stored chat is data, not trusted. The component is ExportDesignLicensingCard.tsx (a distinct
// name on purpose, plan §0.5).

export type LicensingVerdict = 'required' | 'not_required' | 'uncertain';

export interface LicensingCheck {
  label: string;
  value: string;
}

export interface LicensingCardPayload {
  country: string;
  verdict: LicensingVerdict;
  regime: string;
  /** "ECCN 3A001.b.2.e" / "USML Category XII(a)(1)", or null when the classification names none. */
  control?: string | null;
  /** The primary ECCN's reasons for control ("NS", "RS"…); EAR-listed only. */
  reasons: string[];
  /** The model's grounded reasons, cleaned by the backend. */
  why: string[];
  /** Built only from the deterministic checks (Country Chart, Part 746, §126.1). */
  checks: LicensingCheck[];
  warnings: string[];
  exceptions_note?: string | null;
  explanation?: string | null;
}

const VERDICTS: LicensingVerdict[] = ['required', 'not_required', 'uncertain'];

const isString = (value: unknown): value is string => typeof value === 'string';
const isObject = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => isString(v) && Boolean(v.trim())) : [];
const optional = (value: unknown): string | null => (isString(value) && value.trim() ? value : null);

/** A licensing card from a frame or a stored chat, or undefined when it isn't one. */
export const readLicensingCard = (raw: unknown): LicensingCardPayload | undefined => {
  if (!isObject(raw) || !isString(raw.country) || !raw.country.trim()) return undefined;
  return {
    country: raw.country,
    verdict: VERDICTS.includes(raw.verdict) ? (raw.verdict as LicensingVerdict) : 'uncertain',
    regime: isString(raw.regime) && raw.regime.trim() ? raw.regime : 'uncertain',
    control: optional(raw.control),
    reasons: strings(raw.reasons),
    why: strings(raw.why),
    checks: (Array.isArray(raw.checks) ? raw.checks : [])
      .filter((c: any) => isObject(c) && isString(c.label) && isString(c.value) && c.label.trim() && c.value.trim())
      .map((c: any) => ({ label: c.label, value: c.value })),
    warnings: strings(raw.warnings),
    exceptions_note: optional(raw.exceptions_note),
    explanation: optional(raw.explanation),
  };
};

export const VERDICT_PILLS: Record<LicensingVerdict, { label: string; className: string }> = {
  required: { label: 'Licence needed', className: 'is-warn' },
  not_required: { label: 'No licence needed', className: 'is-ok' },
  uncertain: { label: 'Not settled', className: 'is-neutral' },
};

/** "Japan: a licence is needed": the country and the answer, like the result card leads with the control. */
export const licensingTitleOf = (card: LicensingCardPayload): string => {
  const country = cardText(card.country);
  if (card.verdict === 'required') return `${country}: a licence is needed`;
  if (card.verdict === 'not_required') return `${country}: no licence needed on what was checked`;
  return `${country}: whether a licence is needed isn't settled`;
};

/** What the answer rests on and who licenses it, from the classification (board 14 note 1). */
export const licensingSubOf = (card: LicensingCardPayload): string => {
  const control = cardText(card.control);
  if (card.regime === 'EAR-listed') {
    const under = control ? `EAR-listed under ${control}` : 'EAR-listed';
    const why = card.reasons.length > 0 ? `, controlled for ${reasonPhrase(card.reasons)}` : '';
    return `${under}${why}. Commerce (BIS) licenses it, through SNAP-R.`;
  }
  if (card.regime === 'ITAR') {
    return `${control ? `ITAR under ${control}` : 'ITAR'}. State (DDTC) licenses it.`;
  }
  if (card.regime === 'EAR99') return 'EAR99: subject to the EAR but on no Commerce Control List entry.';
  return 'The classification this check rests on is not settled.';
};
